import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {createPortal} from 'react-dom';

import {ToastContainer} from '@stubhub/bamboo';
import {useSmartBannerUpdater} from '@stubhub/hook-utils';
import FeatureToggle from '@stubhub/react-feature-toggle';
import VirtualAssistantLazyLoad from '@stubhub/react-lazy-virtual-assistant';
import {UnifiedLayoutWithRouter} from '@stubhub/react-unified-app';
import {get} from '@stubhub/rest-method';

import ContextProvider from './context';
import FEATURE_FLAGS from './utils/featureFlags';

import './layout.scss';

const styles = {
  openChat: {color: 'black', backgroundColor: '#DAFAFC'},
  closeChat: {color: 'black', backgroundColor: '#DAFAFC'},
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      keepPreviousData: true,
    },
  },
});

const Layout = (
  props,
  {
    globalRegistry: {
      gsConfig: {features},
    },
    cookies,
  }
) => {
  const chatbotEnabled = features[FEATURE_FLAGS.mya.chatbot];
  const virtualAssistantEnabled = features[FEATURE_FLAGS.mya.voiceFlow];
  const [auth, setAuth] = useState(false);
  const [customerName, setCustomerName] = useState();
  const [widgetConfiguration, setWidgetConfiguration] = useState({});
  const {defaultLocale: locale} = props;
  const projectID = process.env.REACT_APP_VOICE_FLOW_PROJECT_ID;
  const userGUID = cookies?.get('session_userGUID');

  useSmartBannerUpdater();

  const fetchToken = useCallback(async () => {
    if (!chatbotEnabled) {
      return;
    }
    const fetchToken = async () => {
      try {
        const {token, contact, widgetConfiguration} = await get({
          host: process.env.REACT_APP_API_HOST,
          path: `/getToken?locale=${locale}`,
          json: true,
        });
        setCustomerName(contact.name.firstName);
        setAuth(token);
        setWidgetConfiguration(widgetConfiguration);
      } catch (e) {
        console.error('Could not start amazon connect widget.', e);
      }
    };
    fetchToken();
  }, [chatbotEnabled, locale]);

  useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  const toastPortal = __CLIENT__ && createPortal(<ToastContainer />, document.body);

  return (
    <QueryClientProvider client={queryClient}>
      <UnifiedLayoutWithRouter>
        {auth && (
          <FeatureToggle name={FEATURE_FLAGS.mya.chatbot}>
            {virtualAssistantEnabled ? (
              <VirtualAssistantLazyLoad
                type="VOICE_FLOW"
                projectID={projectID}
                userName={customerName}
                GUID={userGUID}
                locale={locale}
              />
            ) : (
              <VirtualAssistantLazyLoad
                type="AMAZON_CONNECT"
                auth={auth}
                customerName={customerName}
                styles={styles}
                widgetId={widgetConfiguration.widgetId}
                snippetId={widgetConfiguration.snippetId}
                amazonConnectURL={widgetConfiguration.url}
              />
            )}
          </FeatureToggle>
        )}
        <ContextProvider>{props.children}</ContextProvider>
        <FeatureToggle name={FEATURE_FLAGS.mya.RQDevTools}>
          <ReactQueryDevtools initialIsOpen={false} />
        </FeatureToggle>
        {toastPortal}
      </UnifiedLayoutWithRouter>
    </QueryClientProvider>
  );
};

Layout.propTypes = {
  children: PropTypes.node,
  defaultLocale: PropTypes.string,
};

Layout.contextTypes = {
  globalRegistry: PropTypes.object,
  cookies: PropTypes.object,
};

export default Layout;
